// dither.ts — the ENGINE TWIN: a byte-faithful TypeScript port of the firmware
// dither engine (src/dither.cpp + src/pixel.h). The Look portal renders a Look
// through this so the preview is EXACTLY what the camera produces (ADR-0028).
//
// Faithfulness rules (the whole point — a divergence here makes the preview lie):
//   * The ESP32 engine is float32 throughout; JS numbers are float64. Every C++
//     float op is mirrored with Math.fround (`f` below), left-to-right, and the
//     error-diffusion buffers are Float32Array (stores auto-round to float32).
//   * Every C++ (int)/(uint8_t) cast and integer `/` truncates toward zero →
//     Math.trunc, never Math.round/Math.floor.
//   * Input is the camera's RGB565 BIG-ENDIAN frame ([hi,lo] pairs), 5-bit→248
//     expansion (NOT a 0..255 rescale). The dither scratch is BGR.
//   * ditherCycle order is load-bearing for the persisted style index (ADR-0007).
//
// MAINTENANCE: editing src/dither.cpp / src/pixel.h dither constants means
// regenerating the goldens AND porting the change here in the SAME commit
// (ADR-0028). The golden-vector parity test pins every (fixture,palette,style).

import { BLUE_NOISE_M, BLUE_NOISE_N } from "./blueNoise";
import { SRGB_TO_LINEAR } from "./srgbLinear";

const f = Math.fround;

// ---- constants (mirror src/config.h + src/dither.cpp) ----
export const MAX_COLORS_PER_PALETTE = 64;
export const SYMBOL_CELL = 5;
export const NUM_SYMBOL_GLYPHS = 7;

// ditherCycle[] — new styles appended last so the persisted styleIndex stays stable
// (ADR-0007): ASCII = 8, BlueNoise = 9.
export const STYLE_NAMES = [
  "Nearest", "Bayer", "Floyd", "Atkinson",
  "Sierra", "Ordered2", "Halftone", "Block",
  "ASCII", "BlueNoise",
] as const;
export const NUM_STYLES = STYLE_NAMES.length;

// ASCII glyph ramp — baked identical to SYMBOL_GLYPHS in dither.cpp (rows are 5 wide,
// LSB = column 0). Sparsest -> densest.
const SYMBOL_GLYPHS: ReadonlyArray<ReadonlyArray<number>> = [
  [0x00, 0x00, 0x00, 0x00, 0x00], // 0
  [0x00, 0x00, 0x00, 0x04, 0x00], // 1
  [0x00, 0x04, 0x00, 0x04, 0x00], // 2
  [0x00, 0x04, 0x04, 0x04, 0x00], // 3
  [0x04, 0x02, 0x02, 0x04, 0x00], // 4
  [0x04, 0x02, 0x0a, 0x0c, 0x00], // 5
  [0x00, 0x08, 0x0c, 0x0e, 0x04], // 6
];
// Alternate ASCII glyph ramps (per-Look glyphSet; graduated to the firmware GlyphSet enum, #164 P5).
// 7 levels sparsest->densest, 5 bits/row, LSB = column 0. "dots" = SYMBOL_GLYPHS (default → byte-exact goldens).
const BLOCK_GLYPHS: ReadonlyArray<ReadonlyArray<number>> = [
  [0x00, 0x00, 0x00, 0x00, 0x00], [0x00, 0x00, 0x00, 0x00, 0x0e], [0x00, 0x00, 0x00, 0x00, 0x1f],
  [0x00, 0x00, 0x00, 0x1f, 0x1f], [0x00, 0x00, 0x1f, 0x1f, 0x1f], [0x00, 0x1f, 0x1f, 0x1f, 0x1f],
  [0x1f, 0x1f, 0x1f, 0x1f, 0x1f],
]; // bottom-up fill (level meter / ▁▂▃▄▅▆█)
const LINE_GLYPHS: ReadonlyArray<ReadonlyArray<number>> = [
  [0x00, 0x00, 0x00, 0x00, 0x00], [0x00, 0x00, 0x1f, 0x00, 0x00], [0x00, 0x1f, 0x00, 0x1f, 0x00],
  [0x1f, 0x00, 0x1f, 0x00, 0x1f], [0x1f, 0x1f, 0x00, 0x1f, 0x1f], [0x1f, 0x1f, 0x1f, 0x1f, 0x1f],
  [0x1f, 0x1f, 0x1f, 0x1f, 0x1f],
]; // horizontal scanlines (venetian / CRT)
// Typed-character ramp " .-:=+%*#@" (ordered by ink density, sparse->dense). The glyphs are the
// real 8x8 IBM-PC font (dhepper/font8x8 "font8x8_basic", public domain / CC0). 8 bits/row,
// LSB = leftmost column (matches this engine), so the bytes are used verbatim.
const CHAR_GLYPHS: ReadonlyArray<ReadonlyArray<number>> = [
  [0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00], // ' '
  [0x00, 0x00, 0x00, 0x00, 0x00, 0x0c, 0x0c, 0x00], // '.'
  [0x00, 0x00, 0x00, 0x3f, 0x00, 0x00, 0x00, 0x00], // '-'
  [0x00, 0x0c, 0x0c, 0x00, 0x00, 0x0c, 0x0c, 0x00], // ':'
  [0x00, 0x00, 0x3f, 0x00, 0x00, 0x3f, 0x00, 0x00], // '='
  [0x00, 0x0c, 0x0c, 0x3f, 0x0c, 0x0c, 0x00, 0x00], // '+'
  [0x00, 0x63, 0x33, 0x18, 0x0c, 0x66, 0x63, 0x00], // '%'
  [0x00, 0x66, 0x3c, 0xff, 0x3c, 0x66, 0x00, 0x00], // '*'
  [0x36, 0x36, 0x7f, 0x36, 0x7f, 0x36, 0x36, 0x00], // '#'
  [0x3e, 0x63, 0x7b, 0x7b, 0x7b, 0x03, 0x1e, 0x00], // '@'
];
// Longer 16-level font ramp " .-:~=+co$*SwE@M" (same font8x8, density-ordered, evenly spread).
const CHAR16_GLYPHS: ReadonlyArray<ReadonlyArray<number>> = [
  [0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00], // ' '
  [0x00, 0x00, 0x00, 0x00, 0x00, 0x0c, 0x0c, 0x00], // '.'
  [0x00, 0x00, 0x00, 0x3f, 0x00, 0x00, 0x00, 0x00], // '-'
  [0x00, 0x0c, 0x0c, 0x00, 0x00, 0x0c, 0x0c, 0x00], // ':'
  [0x6e, 0x3b, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00], // '~'
  [0x00, 0x00, 0x3f, 0x00, 0x00, 0x3f, 0x00, 0x00], // '='
  [0x00, 0x0c, 0x0c, 0x3f, 0x0c, 0x0c, 0x00, 0x00], // '+'
  [0x00, 0x00, 0x1e, 0x33, 0x03, 0x33, 0x1e, 0x00], // 'c'
  [0x00, 0x00, 0x1e, 0x33, 0x33, 0x33, 0x1e, 0x00], // 'o'
  [0x0c, 0x3e, 0x03, 0x1e, 0x30, 0x1f, 0x0c, 0x00], // '$'
  [0x00, 0x66, 0x3c, 0xff, 0x3c, 0x66, 0x00, 0x00], // '*'
  [0x1e, 0x33, 0x07, 0x0e, 0x38, 0x33, 0x1e, 0x00], // 'S'
  [0x00, 0x00, 0x63, 0x6b, 0x7f, 0x7f, 0x36, 0x00], // 'w'
  [0x7f, 0x46, 0x16, 0x1e, 0x16, 0x46, 0x7f, 0x00], // 'E'
  [0x3e, 0x63, 0x7b, 0x7b, 0x7b, 0x03, 0x1e, 0x00], // '@'
  [0x63, 0x77, 0x7f, 0x7f, 0x6b, 0x63, 0x63, 0x00], // 'M'
];
// A glyph set = its bitmaps + their native cell DIMENSION (5 for the dot ramps, 8 for the font).
interface GlyphSet { dim: number; glyphs: ReadonlyArray<ReadonlyArray<number>>; }
const GLYPH_SETS: Record<string, GlyphSet> = {
  dots: { dim: SYMBOL_CELL, glyphs: SYMBOL_GLYPHS },
  blocks: { dim: SYMBOL_CELL, glyphs: BLOCK_GLYPHS },
  lines: { dim: SYMBOL_CELL, glyphs: LINE_GLYPHS },
  characters: { dim: 8, glyphs: CHAR_GLYPHS },
  characters16: { dim: 8, glyphs: CHAR16_GLYPHS },
};

/** Mapping mode — how a pixel picks its Palette colour (src/palette.h MapMode; ADR-0033). */
export type MapMode = "nearest" | "luminance" | "gradient";

/** A Palette: the firmware Palette struct (src/palette.h), colors as [n][3] RGB. */
export interface Palette {
  name: string;
  colors: ReadonlyArray<readonly [number, number, number]>;
  colorCount: number;
  mapMode?: MapMode;       // nearest (default/absent) | luminance | gradient
  brightness: number;
  contrast: number;
  saturation: number;
  // Per-Look colour + dither ops, GRADUATED into the firmware engine + the Look-Contract (#164): P4
  // tone ops (gamma/negative/solarize/posterize/hueRotate) + P5 dither/ASCII knobs. All optional and
  // DEFAULT to identity, so an absent field leaves output byte-identical to the goldens (ADR-0028).
  gamma?: number;          // tone curve, percent; 100 (or absent) = identity
  negative?: boolean;      // invert tones
  solarize?: boolean;      // invert above mid-luma (Sabattier)
  posterize?: number;      // N tone levels per channel; 0/1 (or absent) = off
  hueRotate?: number;      // hue rotation in degrees; 0 (or absent) = identity
  ditherStrength?: number; // ordered+ED dither amplitude, PERCENT; 100 (or absent) = identity, 0..200
  ditherBias?: number;     // ordered+ED flat lean before quantize; 0 (or absent) = none, -64..64
  glyphSet?: string;       // ASCII glyph ramp: dots|blocks|lines|characters|characters16 (default dots)
  glyphSize?: number;      // ASCII glyph cell, PERCENT of native dim; 100 (or absent) = native, 50..300
  // Luma Key / Chroma Key / Splash (prototype, #235). Optional; absent/0 = identity (goldens unaffected).
  lumaKey?: number;        // luma-key threshold 0..255; 0 (or absent) = off. Below it, desaturate to neutral.
  chromaKey?: number;      // chroma-key threshold 0..255; 0 = off. Desaturate pixels whose chroma (max-min) is BELOW it.
  splash?: number;         // nearest-mode splash 0..100; 0 = off. Pixels farther than this (Oklab keep-radius) from every palette colour snap to the nearest GREY palette entry.
  // Vignette (#238) — the first SPATIAL Look field. Optional; absent/0 = off (goldens unaffected).
  vignette?: number;        // signed strength -100..100; + darkens rim (spotlight), - brightens (halo)
  vignetteRadius?: number;  // 0..100, size of the untouched centre
  vignetteFeather?: number; // 0..100, softness of the clean->burn transition
}

// ===========================================================================
//  pixel helpers (src/pixel.h) — pure, integer, byte-order-exact.
// ===========================================================================

export function clampInt(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
function clampF(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
/** (299r + 587g + 114b) / 1000, integer floor-div (matches rgbLuminanceInt). */
export function rgbLuminanceInt(r: number, g: number, b: number): number {
  return Math.trunc((299 * r + 587 * g + 114 * b) / 1000);
}
/** RGB565 [hi,lo] at pixel (x,y) -> 8-bit channels via <<3,<<2,<<3 (0..248/252). */
export function readRGB565Pixel(buf: Uint8Array, W: number, x: number, y: number): [number, number, number] {
  const idx = (y * W + x) * 2;
  const px = (buf[idx]! << 8) | buf[idx + 1]!;
  return [((px >> 11) & 0x1f) << 3, ((px >> 5) & 0x3f) << 2, (px & 0x1f) << 3];
}
/** Write RGB888 into the BGR scratch (out[0]=b, out[1]=g, out[2]=r). */
function writeBGR(out: Uint8Array, W: number, x: number, y: number, r: number, g: number, b: number): void {
  const oi = (y * W + x) * 3;
  out[oi] = b; out[oi + 1] = g; out[oi + 2] = r;
}

// ---- Per-Look colour/tone/dither ops, GRADUATED to firmware + the Look-Contract (#164). All identity
//      by default, so an absent field leaves the goldens untouched. ----
// ---- Deterministic, libm-free float math: byte-identical to firmware (src/dither.cpp), ADR-0034.
// pow()/cbrt() are not bit-portable across V8 / libm / the ESP32 LX6, so the GLOBAL Oklab match +
// linear-light ED use a baked sRGB<->linear table + a shared cube root (every op float32-pinned;
// double-rounding is innocuous for + - * /, so f(...) == the firmware's float ops with FMA off).
const _cbrtF = new Float32Array(1);
const _cbrtU = new Uint32Array(_cbrtF.buffer);
/** float32 cube root (x >= 0), op-for-op identical to firmware cbrtDet (bit hack + 3 Newton steps). */
function cbrtDet(x: number): number {
  x = f(x);
  if (x <= 0) return 0;
  _cbrtF[0] = x;
  _cbrtU[0] = (Math.trunc(_cbrtU[0]! / 3) + 0x2a514067) >>> 0;
  let y = _cbrtF[0]!;
  for (let k = 0; k < 3; k++) {           // Newton: y <- (2y + x/y^2)/3
    const yy = f(y * y);
    const t = f(x / yy);
    const twoY = f(y + y);
    const s = f(twoY + t);
    y = f(s / 3);
  }
  return y;
}
// Oklab matrix constants, pre-rounded to float32 so the products match the firmware's `float` literals.
const KL0 = f(0.4122214708), KL1 = f(0.5363325363), KL2 = f(0.0514459929);
const KM0 = f(0.2119034982), KM1 = f(0.6806995451), KM2 = f(0.1073969566);
const KS0 = f(0.0883024619), KS1 = f(0.2817188376), KS2 = f(0.6299787005);
const JL0 = f(0.2104542553), JL1 = f(0.7936177850), JL2 = f(0.0040720468);
const JA0 = f(1.9779984951), JA1 = f(2.4285922050), JA2 = f(0.4505937099);
const JB0 = f(0.0259040371), JB1 = f(0.7827717662), JB2 = f(0.8086757660);
/** sRGB byte triple -> Oklab [L,a,b] (baked linear LUT + cbrtDet), byte-identical to firmware. */
function srgbToOklab(r: number, g: number, b: number): [number, number, number] {
  const rl = SRGB_TO_LINEAR[r & 0xff]!, gl = SRGB_TO_LINEAR[g & 0xff]!, bl = SRGB_TO_LINEAR[b & 0xff]!;
  const l = f(f(f(KL0 * rl) + f(KL1 * gl)) + f(KL2 * bl));
  const m = f(f(f(KM0 * rl) + f(KM1 * gl)) + f(KM2 * bl));
  const s = f(f(f(KS0 * rl) + f(KS1 * gl)) + f(KS2 * bl));
  const l_ = cbrtDet(l), m_ = cbrtDet(m), s_ = cbrtDet(s);
  return [
    f(f(f(JL0 * l_) + f(JL1 * m_)) - f(JL2 * s_)),
    f(f(f(JA0 * l_) - f(JA1 * m_)) + f(JA2 * s_)),
    f(f(f(JB0 * l_) + f(JB1 * m_)) - f(JB2 * s_)),
  ];
}

// Deterministic float32 pow(x,e), x in [0,1], e in [0.5,2.0] — op-for-op identical to firmware powDet
// (binary-sqrt: x^e = x^floor(e) * prod(sqrt^k(x) for set bits of frac(e))). sqrt is IEEE-portable;
// double-rounding is innocuous for sqrt, so f(Math.sqrt) == sqrtf. Used to BUILD the gamma LUT.
function powDet(x: number, e: number): number {
  x = f(x);
  if (x <= 0) return 0;
  const i = Math.trunc(e); let fr = f(e - i);
  let r = 1;
  if (i >= 1) r = f(r * x);
  if (i >= 2) r = f(r * x);
  let s = x;
  for (let k = 0; k < 20; k++) {
    s = f(Math.sqrt(s));
    fr = f(fr * 2); const bit = Math.trunc(fr); fr = f(fr - bit);
    if (bit) r = f(r * s);
  }
  return r;
}
// 256-entry gamma curve, memoized per gamma % (matches firmware buildGammaLut + gammaApply LUT+interp).
let _gammaLut: Float32Array | null = null;
let _gammaLutFor = -1;
function gammaApply(v: number, pct: number): number {
  if (!_gammaLut || _gammaLutFor !== pct) {
    const lut = new Float32Array(256);
    const e = f(100 / (pct < 1 ? 100 : pct));
    for (let u = 0; u < 256; u++) lut[u] = f(255 * powDet(f(u / 255), e));
    _gammaLut = lut; _gammaLutFor = pct;
  }
  const x = clampF(v, 0, 255);
  let i = Math.trunc(x); if (i > 254) i = 254;
  const frac = f(x - i);
  return f(_gammaLut[i]! + f(frac * f(_gammaLut[i + 1]! - _gammaLut[i]!)));
}
/** Posterize one channel to N tone levels (N>=2). round-half-up = trunc(x+0.5), matching firmware. */
function posterizeChannel(v: number, levels: number): number {
  const n = levels - 1;
  const t = f(f(clampF(v, 0, 255)) / 255);
  const q = Math.trunc(f(f(t * n) + 0.5));
  return f(f(q / n) * 255);
}
/** HSL hue->channel helper (mirrors firmware hue2; f32-pinned thresholds + ops). */
function hue2(p: number, q: number, t: number): number {
  if (t < 0) t = f(t + 1);
  if (t > 1) t = f(t - 1);
  if (t < f(1 / 6)) return f(p + f(f(f(q - p) * 6) * t));
  if (t < f(1 / 2)) return q;
  if (t < f(2 / 3)) return f(p + f(f(f(q - p) * f(f(2 / 3) - t)) * 6));
  return p;
}
/** Rotate hue by `deg` degrees via an HSL round-trip — float32-pinned op-for-op to firmware rotateHue. */
function rotateHue(r: number, g: number, b: number, deg: number): [number, number, number] {
  const rn = f(r / 255), gn = f(g / 255), bn = f(b / 255);
  const mx = Math.max(rn, gn, bn), mn = Math.min(rn, gn, bn);
  const l = f(f(mx + mn) / 2), d = f(mx - mn);
  let h = 0, s = 0;
  if (d !== 0) {
    s = l > 0.5 ? f(d / f(f(2 - mx) - mn)) : f(d / f(mx + mn));
    if (mx === rn) h = f(f(f(gn - bn) / d) + (gn < bn ? 6 : 0));
    else if (mx === gn) h = f(f(f(bn - rn) / d) + 2);
    else h = f(f(f(rn - gn) / d) + 4);
    h = f(h / 6);
  }
  h = f(h + f(deg / 360)); h = f(h - Math.trunc(h)); if (h < 0) h = f(h + 1);
  if (s === 0) { const v = f(l * 255); return [v, v, v]; }
  const q = l < 0.5 ? f(l * f(1 + s)) : f(f(l + s) - f(l * s)), p = f(f(2 * l) - q);
  return [f(hue2(p, q, f(h + f(1 / 3))) * 255), f(hue2(p, q, h) * 255), f(hue2(p, q, f(h - f(1 / 3))) * 255)];
}

/** The per-Look tone ops (#164 P4), in pipeline order. All identity-gated. gamma is percent (100=id). */
interface Fx { gamma: number; negative: boolean; solarize: boolean; posterize: number; hueRotate: number; lumaKey: number; chromaKey: number; }
function fxOf(p: Palette): Fx {
  return {
    gamma: p.gamma ?? 100, negative: p.negative ?? false, solarize: p.solarize ?? false,
    posterize: p.posterize ?? 0, hueRotate: p.hueRotate ?? 0, lumaKey: p.lumaKey ?? 0, chromaKey: p.chromaKey ?? 0,
  };
}
function applyFx(r: number, g: number, b: number, fx: Fx): [number, number, number] {
  if (fx.gamma !== 100) { r = gammaApply(r, fx.gamma); g = gammaApply(g, fx.gamma); b = gammaApply(b, fx.gamma); }
  if (fx.hueRotate !== 0) { [r, g, b] = rotateHue(r, g, b, fx.hueRotate); }
  if (fx.posterize >= 2) { r = posterizeChannel(r, fx.posterize); g = posterizeChannel(g, fx.posterize); b = posterizeChannel(b, fx.posterize); }
  if (fx.solarize) { r = r > 127.5 ? f(255 - r) : r; g = g > 127.5 ? f(255 - g) : g; b = b > 127.5 ? f(255 - b) : b; }
  if (fx.negative) { r = f(255 - r); g = f(255 - g); b = f(255 - b); }
  // Luma Key (prototype, #235): below the threshold, desaturate to neutral so shadows land on the
  // palette's grays (nearest mapping). Keys off the FINAL adjusted luminance. 0 = off.
  if (fx.lumaKey > 0) {
    const lum = f(f(f(r * 0.299) + f(g * 0.587)) + f(b * 0.114));
    if (lum < fx.lumaKey) { r = lum; g = lum; b = lum; }
  }
  // Chroma Key (prototype, #235): desaturate LOW-chroma (washed-out) pixels so a saturated subject survives
  // while a hazy/desaturated background greys out. Chroma = max-min channel. Needs nearest mapping. 0 = off.
  if (fx.chromaKey > 0) {
    const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
    if (f(mx - mn) < fx.chromaKey) { const lum = f(f(f(r * 0.299) + f(g * 0.587)) + f(b * 0.114)); r = lum; g = lum; b = lum; }
  }
  return [r, g, b];
}

// ===========================================================================
//  adjustRGB (dither.cpp) — brightness -> SMPTE contrast -> sat, all float32.
//  Returns truncated 0..255 ints. b/c/s are the active palette's values.
//  `fx` carries the preview-pilot colour ops (applied after sat; identity by default).
// ===========================================================================
export function adjustRGB(
  r: number, g: number, b: number,
  brightness: number, contrast: number, saturation: number,
  fx?: Fx,
): [number, number, number] {
  const br = f(f(brightness * 255) / 100);
  let rf = f(r + br), gf = f(g + br), bf = f(b + br);
  const c = f(contrast / 100);
  const factor = f(f(259 * f(f(c * 255) + 255)) / f(255 * f(259 - f(c * 255))));
  rf = f(f(factor * f(rf - 128)) + 128);
  gf = f(f(factor * f(gf - 128)) + 128);
  bf = f(f(factor * f(bf - 128)) + 128);
  const sat = f(1 + f(saturation / 100));
  const gray = f(f(f(rf * 0.299) + f(gf * 0.587)) + f(bf * 0.114));
  rf = f(gray + f(sat * f(rf - gray)));
  gf = f(gray + f(sat * f(gf - gray)));
  bf = f(gray + f(sat * f(bf - gray)));
  // preview-pilot colour ops (each gated to identity, so default Looks/goldens are unaffected).
  if (fx) { [rf, gf, bf] = applyFx(rf, gf, bf, fx); }
  return [Math.trunc(clampF(rf, 0, 255)), Math.trunc(clampF(gf, 0, 255)), Math.trunc(clampF(bf, 0, 255))];
}

// ===========================================================================
//  quant LUT (dither.cpp `buildQuantLut`) — 32768 entries (5-bit/chan). Returns { lut, paper }.
//  paper = the palette's darkest color (by luminance), for the ASCII pass.
// ===========================================================================
interface Quant {
  lut: Uint8Array;
  paper: [number, number, number];
}
function buildQuantLut(p: Palette): Quant {
  // Darkest palette colour (first by luminance) = the ASCII "paper".
  let bestL = 1 << 30, bi = 0;
  for (let i = 0; i < p.colorCount; i++) {
    const c = p.colors[i]!;
    const l = rgbLuminanceInt(c[0], c[1], c[2]);
    if (l < bestL) { bestL = l; bi = i; }
  }
  const pc = p.colors[bi]!;
  const paper: [number, number, number] = [pc[0], pc[1], pc[2]];

  // Perceptual nearest-colour match runs in Oklab (global; ADR-0033/0034). Precompute palette Oklab.
  const oklab = p.colors.map((c) => srgbToOklab(c[0], c[1], c[2]));

  // order[]: identity, or palette sorted asc by luminance (bubble sort, strict lj<li
  // swap → stable on ties — must NOT use Array.sort, which reorders ties differently).
  const order = new Array<number>(p.colorCount);
  for (let i = 0; i < p.colorCount; i++) order[i] = i;
  if (p.mapMode === "luminance") {
    for (let i = 0; i < p.colorCount - 1; i++)
      for (let j = i + 1; j < p.colorCount; j++) {
        const ci = p.colors[order[i]!]!, cj = p.colors[order[j]!]!;
        const li = rgbLuminanceInt(ci[0], ci[1], ci[2]);
        const lj = rgbLuminanceInt(cj[0], cj[1], cj[2]);
        if (lj < li) { const t = order[i]!; order[i] = order[j]!; order[j] = t; }
      }
  }

  const lut = new Uint8Array(32768);
  for (let r5 = 0; r5 < 32; r5++)
    for (let g5 = 0; g5 < 32; g5++)
      for (let b5 = 0; b5 < 32; b5++) {
        const r = r5 << 3, g = g5 << 3, b = b5 << 3;
        let idx: number;
        if (p.mapMode === "luminance") {
          const level = Math.trunc((rgbLuminanceInt(r, g, b) * p.colorCount) / 256);
          idx = order[clampInt(level, 0, p.colorCount - 1)]!;
        } else if (p.mapMode === "gradient") {
          // authored-order ramp: bucket luminance, index the palette as written (NOT sorted),
          // so a non-monotonic colourmap (rainbow/jet) keeps its cold->hot sequence.
          const level = Math.trunc((rgbLuminanceInt(r, g, b) * p.colorCount) / 256);
          idx = clampInt(level, 0, p.colorCount - 1);
        } else {                                          // nearest: perceptual Oklab (global)
          const [L, A, B] = srgbToOklab(r, g, b);
          let best = Infinity; idx = 0;                   // strict d<best → first index wins ties
          for (let i = 0; i < p.colorCount; i++) {
            const o = oklab[i]!;
            const dL = f(L - o[0]!), dA = f(A - o[1]!), dB = f(B - o[2]!);
            const d = f(f(f(dL * dL) + f(dA * dA)) + f(dB * dB));
            if (d < best) { best = d; idx = i; }
          }
          // Splash (#235): too far from every palette colour -> snap to the palette's nearest GREY entry
          // (index-plane native, so firmware + Clips match; a 1-byte index can't carry an off-palette grey).
          // Keep-radius in Oklab (0..1); the pixel's own luminance picks the grey.
          if (p.splash) {
            const kr = f(p.splash / 100);
            if (best > f(kr * kr)) {
              const lum = rgbLuminanceInt(r, g, b);
              const [gL, gA, gB] = srgbToOklab(lum, lum, lum);
              let gb = Infinity;
              for (let i = 0; i < p.colorCount; i++) {
                const o = oklab[i]!;
                const dL = f(gL - o[0]!), dA = f(gA - o[1]!), dB = f(gB - o[2]!);
                const dd = f(f(f(dL * dL) + f(dA * dA)) + f(dB * dB));
                if (dd < gb) { gb = dd; idx = i; }
              }
            }
          }
        }
        lut[(r5 << 10) | (g5 << 5) | b5] = idx;
      }
  return { lut, paper };
}
function quantize(p: Palette, lut: Uint8Array, r: number, g: number, b: number): [number, number, number] {
  const idx = lut[((r >> 3) << 10) | ((g >> 3) << 5) | (b >> 3)]!;
  const c = p.colors[idx]!;
  return [c[0], c[1], c[2]];
}

// ===========================================================================
//  the 9 dither methods (dither.cpp `processPalette*`). Each writes BGR into `out`.
// ===========================================================================

// #238 vignette — a byte-EXACT mirror of src/vignette.cpp. Pure integer: Math.trunc matches C++ integer
// division (truncate toward zero) and float64 holds these magnitudes exactly, so no f()/fround is needed.
export function vignetteFactorQ8(d2: number, rad2: number, strength: number, radius: number, feather: number): number {
  if (strength === 0 || rad2 <= 0) return 256;
  let dn = Math.trunc((d2 * 4096) / rad2);
  if (dn > 4096) dn = 4096; else if (dn < 0) dn = 0;
  const rs = Math.trunc((radius * 4096) / 100);
  const lo = rs - Math.trunc((rs * feather) / 100);
  if (dn <= lo) return 256;
  const span = 4096 - lo;
  if (span <= 0) return 256;
  let t = Math.trunc(((dn - lo) * 4096) / span);
  if (t > 4096) t = 4096;
  const s = Math.trunc((t * t * (3 * 4096 - 2 * t)) / (4096 * 4096));
  let factor = 256 - Math.trunc((strength * 256 * s) / (100 * 4096));
  if (factor < 0) factor = 0; else if (factor > 512) factor = 512;
  return factor;
}
export function buildVignetteField(W: number, H: number, strength: number, radius: number, feather: number): Uint16Array {
  const hw = (W + 1) >> 1, hh = (H + 1) >> 1, field = new Uint16Array(hw * hh); // ceil — centre line for odd dims
  const rad = Math.min(W, H) - 1, rad2 = rad * rad;
  for (let qy = 0; qy < hh; qy++) {
    const dy2 = 2 * qy - (H - 1), dy2sq = dy2 * dy2;
    for (let qx = 0; qx < hw; qx++) {
      const dx2 = 2 * qx - (W - 1);
      field[qy * hw + qx] = vignetteFactorQ8(dx2 * dx2 + dy2sq, rad2, strength, radius, feather);
    }
  }
  return field;
}
function vignetteFieldAt(field: Uint16Array, W: number, H: number, x: number, y: number): number {
  const hw = (W + 1) >> 1, hh = (H + 1) >> 1;
  return field[(y < hh ? y : H - 1 - y) * hw + (x < hw ? x : W - 1 - x)]!;
}
// Apply the Q8 factor after adjustRGB, before quantize (index-plane-safe). No-op when off/absent.
function applyVig(rgb: [number, number, number], x: number, y: number, bcs: BCS): [number, number, number] {
  if (!bcs.vigField) return rgb;
  const factor = vignetteFieldAt(bcs.vigField, bcs.vigW, bcs.vigH, x, y);
  if (factor === 256) return rgb;
  const nr = (rgb[0] * factor + 128) >> 8, ng = (rgb[1] * factor + 128) >> 8, nb = (rgb[2] * factor + 128) >> 8;
  return [nr > 255 ? 255 : nr, ng > 255 ? 255 : ng, nb > 255 ? 255 : nb];
}

function methodNearest(buf: Uint8Array, W: number, H: number, out: Uint8Array, p: Palette, lut: Uint8Array, bcs: BCS): void {
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      const [r, g, b] = readRGB565Pixel(buf, W, x, y);
      const [ar, ag, ab] = applyVig(adjustRGB(r, g, b, bcs.b, bcs.c, bcs.s, bcs.fx), x, y, bcs);
      const [rr, gg, bb] = quantize(p, lut, ar, ag, ab);
      writeBGR(out, W, x, y, rr, gg, bb);
    }
}

function methodBlock(buf: Uint8Array, W: number, H: number, out: Uint8Array, p: Palette, lut: Uint8Array, bcs: BCS, blockSize = 2): void {
  for (let by = 0; by < H; by += blockSize)
    for (let bx = 0; bx < W; bx += blockSize) {
      const blockW = Math.min(blockSize, W - bx), blockH = Math.min(blockSize, H - by);
      let sr = 0, sg = 0, sb = 0;
      for (let dy = 0; dy < blockH; dy++) for (let dx = 0; dx < blockW; dx++) {
        const [r, g, b] = readRGB565Pixel(buf, W, bx + dx, by + dy);
        const [ar, ag, ab] = applyVig(adjustRGB(r, g, b, bcs.b, bcs.c, bcs.s, bcs.fx), bx + dx, by + dy, bcs);
        sr += ar; sg += ag; sb += ab;
      }
      const cnt = Math.max(1, blockW * blockH);
      const [rr, gg, bb] = quantize(p, lut, Math.trunc(sr / cnt), Math.trunc(sg / cnt), Math.trunc(sb / cnt));
      for (let dy = 0; dy < blockH; dy++) for (let dx = 0; dx < blockW; dx++)
        writeBGR(out, W, bx + dx, by + dy, rr, gg, bb);
    }
}

function methodOrdered(buf: Uint8Array, W: number, H: number, out: Uint8Array, p: Palette, lut: Uint8Array, bcs: BCS,
                       mat: ReadonlyArray<number>, matN: number, divisor: number, strength: number): void {
  if (p.colorCount >= 32) strength = f(strength * 0.66);
  if (p.colorCount >= 64) strength = f(strength * 0.5);
  strength = f(strength * bcs.dStrength); // Look knob (1 = native)
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      const [r, g, b] = readRGB565Pixel(buf, W, x, y);
      const [ar, ag, ab] = applyVig(adjustRGB(r, g, b, bcs.b, bcs.c, bcs.s, bcs.fx), x, y, bcs);
      const bias = f(f(f(mat[(y % matN) * matN + (x % matN)]! / divisor) - 0.5) * strength);
      const tb = Math.trunc(bias) + bcs.dBias; // + threshold-bias knob (0 = none)
      const [rr, gg, bb] = quantize(p, lut, clampInt(ar + tb, 0, 255), clampInt(ag + tb, 0, 255), clampInt(ab + tb, 0, 255));
      writeBGR(out, W, x, y, rr, gg, bb);
    }
}
const BAYER_M = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];
const ORDERED2_M = [0, 2, 3, 1];
const HALFTONE_M = [12, 5, 6, 13, 4, 0, 1, 7, 11, 3, 2, 8, 15, 10, 9, 14];

function methodFloyd(buf: Uint8Array, W: number, H: number, out: Uint8Array, p: Palette, lut: Uint8Array, bcs: BCS): void {
  const n = W + 2;
  let cR = new Float32Array(n), cG = new Float32Array(n), cB = new Float32Array(n);
  let nR = new Float32Array(n), nG = new Float32Array(n), nB = new Float32Array(n);
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const [r0, g0, b0] = readRGB565Pixel(buf, W, x, y);
      const [r, g, b] = applyVig(adjustRGB(r0, g0, b0, bcs.b, bcs.c, bcs.s, bcs.fx), x, y, bcs);
      const baseR = r, baseG = g, baseB = b;
      // dBias nests INSIDE its own f() (f(f(base+carried)+dBias)) so the f32 firmware's two chained
      // adds — (r + carried) + fbias — stay byte-exact (a single f() over the 3-way sum would not).
      const rf = clampF(f(f(baseR + cR[x + 1]!) + bcs.dBias), 0, 255), gf = clampF(f(f(baseG + cG[x + 1]!) + bcs.dBias), 0, 255), bf = clampF(f(f(baseB + cB[x + 1]!) + bcs.dBias), 0, 255);
      const qr = rf, qg = gf, qb = bf;
      const [rr, gg, bb] = quantize(p, lut, Math.trunc(qr), Math.trunc(qg), Math.trunc(qb));
      writeBGR(out, W, x, y, rr, gg, bb);
      const refR = rr, refG = gg, refB = bb;
      const er = f(f(rf - refR) * bcs.dStrength), eg = f(f(gf - refG) * bcs.dStrength), eb = f(f(bf - refB) * bcs.dStrength);
      cR[x + 2] = cR[x + 2]! + f(f(er * 7) / 16); cG[x + 2] = cG[x + 2]! + f(f(eg * 7) / 16); cB[x + 2] = cB[x + 2]! + f(f(eb * 7) / 16);
      nR[x] = nR[x]! + f(f(er * 3) / 16); nG[x] = nG[x]! + f(f(eg * 3) / 16); nB[x] = nB[x]! + f(f(eb * 3) / 16);
      nR[x + 1] = nR[x + 1]! + f(f(er * 5) / 16); nG[x + 1] = nG[x + 1]! + f(f(eg * 5) / 16); nB[x + 1] = nB[x + 1]! + f(f(eb * 5) / 16);
      nR[x + 2] = nR[x + 2]! + f(f(er * 1) / 16); nG[x + 2] = nG[x + 2]! + f(f(eg * 1) / 16); nB[x + 2] = nB[x + 2]! + f(f(eb * 1) / 16);
    }
    // swap current<-next, zero next (temp-var swap; a destructuring swap would type the
    // buffers as possibly-undefined under noUncheckedIndexedAccess).
    const t0R = cR; cR = nR; nR = t0R; const t0G = cG; cG = nG; nG = t0G; const t0B = cB; cB = nB; nB = t0B;
    nR.fill(0); nG.fill(0); nB.fill(0);
  }
}

function methodAtkinson(buf: Uint8Array, W: number, H: number, out: Uint8Array, p: Palette, lut: Uint8Array, bcs: BCS): void {
  const pad = W + 4;
  let r0R = new Float32Array(pad), r0G = new Float32Array(pad), r0B = new Float32Array(pad);
  let r1R = new Float32Array(pad), r1G = new Float32Array(pad), r1B = new Float32Array(pad);
  let r2R = new Float32Array(pad), r2G = new Float32Array(pad), r2B = new Float32Array(pad);
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const [r0, g0, b0] = readRGB565Pixel(buf, W, x, y);
      const [r, g, b] = applyVig(adjustRGB(r0, g0, b0, bcs.b, bcs.c, bcs.s, bcs.fx), x, y, bcs);
      const baseR = r, baseG = g, baseB = b;
      const rf = clampF(f(f(baseR + r0R[x + 1]!) + bcs.dBias), 0, 255), gf = clampF(f(f(baseG + r0G[x + 1]!) + bcs.dBias), 0, 255), bf = clampF(f(f(baseB + r0B[x + 1]!) + bcs.dBias), 0, 255);
      const qr = rf, qg = gf, qb = bf;
      const [rr, gg, bb] = quantize(p, lut, Math.trunc(qr), Math.trunc(qg), Math.trunc(qb));
      writeBGR(out, W, x, y, rr, gg, bb);
      const refR = rr, refG = gg, refB = bb;
      const er = f(f(f(rf - refR) * bcs.dStrength) / 8), eg = f(f(f(gf - refG) * bcs.dStrength) / 8), eb = f(f(f(bf - refB) * bcs.dStrength) / 8);
      r0R[x + 2] = r0R[x + 2]! + er; r0G[x + 2] = r0G[x + 2]! + eg; r0B[x + 2] = r0B[x + 2]! + eb;
      r0R[x + 3] = r0R[x + 3]! + er; r0G[x + 3] = r0G[x + 3]! + eg; r0B[x + 3] = r0B[x + 3]! + eb;
      r1R[x] = r1R[x]! + er; r1G[x] = r1G[x]! + eg; r1B[x] = r1B[x]! + eb;
      r1R[x + 1] = r1R[x + 1]! + er; r1G[x + 1] = r1G[x + 1]! + eg; r1B[x + 1] = r1B[x + 1]! + eb;
      r1R[x + 2] = r1R[x + 2]! + er; r1G[x + 2] = r1G[x + 2]! + eg; r1B[x + 2] = r1B[x + 2]! + eb;
      r2R[x + 1] = r2R[x + 1]! + er; r2G[x + 1] = r2G[x + 1]! + eg; r2B[x + 1] = r2B[x + 1]! + eb;
    }
    // rotate r0<-r1<-r2<-(zeroed old r0), mirroring the pointer swap.
    const tR = r0R, tG = r0G, tB = r0B;
    r0R = r1R; r0G = r1G; r0B = r1B; r1R = r2R; r1G = r2G; r1B = r2B; r2R = tR; r2G = tG; r2B = tB;
    r2R.fill(0); r2G.fill(0); r2B.fill(0);
  }
}

function methodSierra(buf: Uint8Array, W: number, H: number, out: Uint8Array, p: Palette, lut: Uint8Array, bcs: BCS): void {
  const pad = W + 3;
  let cR = new Float32Array(pad), cG = new Float32Array(pad), cB = new Float32Array(pad);
  let nR = new Float32Array(pad), nG = new Float32Array(pad), nB = new Float32Array(pad);
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const [r0, g0, b0] = readRGB565Pixel(buf, W, x, y);
      const [r, g, b] = applyVig(adjustRGB(r0, g0, b0, bcs.b, bcs.c, bcs.s, bcs.fx), x, y, bcs);
      const baseR = r, baseG = g, baseB = b;
      const rf = clampF(f(f(baseR + cR[x + 1]!) + bcs.dBias), 0, 255), gf = clampF(f(f(baseG + cG[x + 1]!) + bcs.dBias), 0, 255), bf = clampF(f(f(baseB + cB[x + 1]!) + bcs.dBias), 0, 255);
      const qr = rf, qg = gf, qb = bf;
      const [rr, gg, bb] = quantize(p, lut, Math.trunc(qr), Math.trunc(qg), Math.trunc(qb));
      writeBGR(out, W, x, y, rr, gg, bb);
      const refR = rr, refG = gg, refB = bb;
      const er = f(f(rf - refR) * bcs.dStrength), eg = f(f(gf - refG) * bcs.dStrength), eb = f(f(bf - refB) * bcs.dStrength);
      cR[x + 2] = cR[x + 2]! + f(er * 0.5); cG[x + 2] = cG[x + 2]! + f(eg * 0.5); cB[x + 2] = cB[x + 2]! + f(eb * 0.5);
      nR[x] = nR[x]! + f(er * 0.25); nG[x] = nG[x]! + f(eg * 0.25); nB[x] = nB[x]! + f(eb * 0.25);
      nR[x + 1] = nR[x + 1]! + f(er * 0.25); nG[x + 1] = nG[x + 1]! + f(eg * 0.25); nB[x + 1] = nB[x + 1]! + f(eb * 0.25);
    }
    const t0R = cR; cR = nR; nR = t0R; const t0G = cG; cG = nG; nG = t0G; const t0B = cB; cB = nB; nB = t0B;
    nR.fill(0); nG.fill(0); nB.fill(0);
  }
}

export function glyphForLuminance(lum: number): number {
  const gi = Math.trunc((clampInt(lum, 0, 255) * NUM_SYMBOL_GLYPHS) / 256);
  return clampInt(gi, 0, NUM_SYMBOL_GLYPHS - 1);
}
function methodASCII(buf: Uint8Array, W: number, H: number, out: Uint8Array, p: Palette, lut: Uint8Array, bcs: BCS, paper: [number, number, number]): void {
  // glyphSize (PERCENT, own field #164 P5; no longer overloads dStrength) sets the render cell.
  // INTEGER round-half-up to match the firmware's `(gdim*glyphSize + 50)/100` byte-for-byte (positive
  // inputs only) — Math.round on a float `gdim*pct/100` could differ by an ULP at a .5 boundary.
  const gs = GLYPH_SETS[p.glyphSet ?? "dots"] ?? GLYPH_SETS.dots!; // configurable glyph ramp
  const glyphs = gs.glyphs, gdim = gs.dim, levels = glyphs.length;
  const cell = clampInt(Math.trunc((gdim * (p.glyphSize ?? 100) + 50) / 100), 2, 24); // glyphSize 100% = native dim
  for (let cy = 0; cy < H; cy += cell)
    for (let cx = 0; cx < W; cx += cell) {
      const ch = Math.min(cell, H - cy), cw = Math.min(cell, W - cx);
      let sr = 0, sg = 0, sb = 0, cnt = 0;
      for (let y = 0; y < ch; y++)
        for (let x = 0; x < cw; x++) {
          const [r, g, b] = readRGB565Pixel(buf, W, cx + x, cy + y); // RAW (un-adjusted) average
          sr += r; sg += g; sb += b; cnt++;
        }
      // adjustRGB ONCE on the average (not per source pixel).
      const [ar, ag, ab] = applyVig(adjustRGB(Math.trunc(sr / cnt), Math.trunc(sg / cnt), Math.trunc(sb / cnt), bcs.b, bcs.c, bcs.s, bcs.fx), cx, cy, bcs);
      const [ir, ig, ib] = quantize(p, lut, ar, ag, ab); // ink
      const gi = clampInt(Math.trunc((rgbLuminanceInt(ar, ag, ab) * levels) / 256), 0, levels - 1); // glyph by tone
      for (let y = 0; y < ch; y++) {
        const rowbits = glyphs[gi]![Math.trunc((y * gdim) / cell)]!; // scale glyph -> cell
        for (let x = 0; x < cw; x++) {
          if ((rowbits >> Math.trunc((x * gdim) / cell)) & 1) writeBGR(out, W, cx + x, cy + y, ir, ig, ib);
          else writeBGR(out, W, cx + x, cy + y, paper[0], paper[1], paper[2]);
        }
      }
    }
}

interface BCS { b: number; c: number; s: number; fx: Fx; dStrength: number; dBias: number; vigField?: Uint16Array; vigW: number; vigH: number; }

// ===========================================================================
//  applyDither (dither.cpp `applyDither`/`ditherCore`) — run a style into the BGR
//  scratch, then pack back into the in-place RGB565 frame. Mutates `buf`, returns it.
//
//  TWIN DIVERGENCE (output-identical): the FIRMWARE engine was refactored to write a
//  1-byte palette-INDEX plane and expand index -> RGB565 directly (the index-plane
//  pipeline, #131) — it no longer materializes this BGR RGB888 scratch. The twin keeps
//  the BGR path; both emit byte-identical RGB565 (golden-pinned, ADR-0028), so only the
//  INTERNAL representation differs, never the output. The maintenance rule still holds:
//  a firmware change that moves OUTPUT must be ported here in the same commit + regen goldens.
// ===========================================================================
export function applyDither(buf: Uint8Array, w: number, h: number, style: number, p: Palette): Uint8Array {
  const method = ((style % NUM_STYLES) + NUM_STYLES) % NUM_STYLES; // index into STYLE_NAMES
  const { lut, paper } = buildQuantLut(p);
  // dStrength is now PERCENT (100 = identity), mirroring the firmware `p.ditherStrength/100.0f`; the
  // `f(.../100)` frounds the divided operand so f32 firmware stays byte-exact (#164 P5). dBias is a raw
  // signed int lean. glyphSize is read directly in methodASCII (no longer overloads dStrength).
  const vig = p.vignette ?? 0; // #238 build the ¼-symmetric field once (mirrors the firmware refreshVignetteField)
  const vigField = vig !== 0 ? buildVignetteField(w, h, vig, p.vignetteRadius ?? 0, p.vignetteFeather ?? 0) : undefined;
  const bcs: BCS = { b: p.brightness, c: p.contrast, s: p.saturation, fx: fxOf(p), dStrength: f((p.ditherStrength ?? 100) / 100), dBias: p.ditherBias ?? 0, vigField, vigW: w, vigH: h };
  const out = new Uint8Array(w * h * 3);

  switch (STYLE_NAMES[method]!) {
    case "Nearest":  methodNearest(buf, w, h, out, p, lut, bcs); break;
    case "Bayer":    methodOrdered(buf, w, h, out, p, lut, bcs, BAYER_M, 4, 15, 48); break;
    case "Floyd":    methodFloyd(buf, w, h, out, p, lut, bcs); break;
    case "Atkinson": methodAtkinson(buf, w, h, out, p, lut, bcs); break;
    case "Sierra":   methodSierra(buf, w, h, out, p, lut, bcs); break;
    case "Ordered2": methodOrdered(buf, w, h, out, p, lut, bcs, ORDERED2_M, 2, 3, 40); break;
    case "Halftone": methodOrdered(buf, w, h, out, p, lut, bcs, HALFTONE_M, 4, 15, 44); break;
    case "Block":    methodBlock(buf, w, h, out, p, lut, bcs); break;
    case "ASCII":    methodASCII(buf, w, h, out, p, lut, bcs, paper); break;
    // BlueNoise: ordered dithering through a 64x64 void-and-cluster mask (vs Bayer's 4x4).
    // divisor = maxValue (N*N-1), matching Bayer (0..15 / 15) — see methodOrdered.
    case "BlueNoise": methodOrdered(buf, w, h, out, p, lut, bcs, BLUE_NOISE_M, BLUE_NOISE_N, BLUE_NOISE_N * BLUE_NOISE_N - 1, 48); break;
  }

  // BGR scratch -> RGB565 big-endian, in place. (Firmware packs index -> RGB565 here; same bytes.)
  for (let i = 0; i < w * h; i++) {
    const b = out[i * 3 + 0]!, g = out[i * 3 + 1]!, r = out[i * 3 + 2]!;
    const v = ((r >> 3) << 11) | ((g >> 2) << 5) | (b >> 3);
    buf[i * 2 + 0] = (v >> 8) & 0xff;
    buf[i * 2 + 1] = v & 0xff;
  }
  return buf;
}
